import { INSPECTION_CHECKLIST_ITEMS } from "@/data/inspection-items";
export type InspectionTierValue = "basic" | "enhanced" | "enhanced_obd";

export type InspectionAngleValue = "front" | "rear" | "left" | "right";

/** Values must match the API `InspectionAngle` enum; they are sent as the media caption. */
export const INSPECTION_ANGLES: { value: InspectionAngleValue; label: string }[] = [
  { value: "front", label: "Front" },
  { value: "rear", label: "Rear" },
  { value: "left", label: "Left side" },
  { value: "right", label: "Right side" },
];

export function getMissingAngles(
  media: { type: "photo" | "video"; angle?: InspectionAngleValue }[],
): { value: InspectionAngleValue; label: string }[] {
  const captured = new Set(
    media.filter((item) => item.type === "photo").map((item) => item.angle),
  );

  return INSPECTION_ANGLES.filter((angle) => !captured.has(angle.value));
}

export function getMissingAnglesMessage(
  missing: { label: string }[],
): string {
  return `Photos of all 4 vehicle angles are required. Missing: ${missing
    .map((angle) => angle.label)
    .join(", ")}.`;
}
export const INSPECTION_TIERS = [
  {
    value: "basic" as const,
    label: "Basic Condition Report",
    fee: "£35",
    description:
      "VIN, mileage, warning lights, exterior/interior, visual tyres.",
  },
  {
    value: "enhanced" as const,
    label: "Enhanced Collection",
    fee: "£65",
    description:
      "Basic plus tread readings, fluids, electrics, damage notes, extra media.",
  },
  {
    value: "enhanced_obd" as const,
    label: "Enhanced + OBD",
    fee: "£95",
    description: "Enhanced plus diagnostic scan and fault-code capture.",
  },
] as const;

export type InspectionTierRequirements = {
  minPhotos: number;
  requireOdometer: boolean;
  requireVin: boolean;
  requireWarningLights: boolean;
  requireFuelLevel: boolean;
  requireObd: boolean;
  requireDamageNotes: boolean;
  requiredCategories: string[];
  requireAllItemsInCategories: boolean;
};

export function getTierRequirements(
  tier: InspectionTierValue = "basic",
): InspectionTierRequirements {
  const baseCategories = ["Exterior", "Interior", "Tyres", "Glass & Lights"];
  if (tier === "basic") {
    return {
      minPhotos: 4,
      requireOdometer: true,
      requireVin: true,
      requireWarningLights: true,
      requireFuelLevel: false,
      requireObd: false,
      requireDamageNotes: false,
      requiredCategories: baseCategories,
      requireAllItemsInCategories: false,
    };
  }
  if (tier === "enhanced") {
    return {
      minPhotos: 4,
      requireOdometer: true,
      requireVin: true,
      requireWarningLights: true,
      requireFuelLevel: true,
      requireObd: false,
      requireDamageNotes: true,
      requiredCategories: [...baseCategories, "Mirror & Plate"],
      requireAllItemsInCategories: true,
    };
  }
  return {
    minPhotos: 4,
    requireOdometer: true,
    requireVin: true,
    requireWarningLights: true,
    requireFuelLevel: true,
    requireObd: true,
    requireDamageNotes: true,
    requiredCategories: [...baseCategories, "Mirror & Plate"],
    requireAllItemsInCategories: true,
  };
}

export function getChecklistItemsForCategory(category: string): string[] {
  return INSPECTION_CHECKLIST_ITEMS.filter(
    (item) => item.category === category,
  ).map((item) => item.name);
}

export function validateTierInspection(input: {
  tier: InspectionTierValue;
  photoCount: number;
  media: { type: "photo" | "video"; angle?: InspectionAngleValue }[];
  vin?: string;
  warningLightsPresent?: boolean | null;
  odometerReading?: number;
  fuelLevel?: string;
  obdFaultCodes?: string[] | null;
  checklistItems: {
    item_name: string;
    condition: string;
    remarks?: string;
  }[];
}): string | null {
  const requirements = getTierRequirements(input.tier);
  const itemNames = input.checklistItems.map((item) => item.item_name);

  if (input.photoCount < requirements.minPhotos) {
    return `At least ${requirements.minPhotos} photo(s) are required for this inspection tier.`;
  }

  const missingAngles = getMissingAngles(input.media);
  if (missingAngles.length > 0) {
    return getMissingAnglesMessage(missingAngles);
  }

  if (requirements.requireOdometer && input.odometerReading == null) {
    return "Odometer reading is required for this inspection tier.";
  }

  if (requirements.requireVin && !input.vin?.trim()) {
    return "VIN is required for this inspection tier.";
  }

  if (requirements.requireFuelLevel && !input.fuelLevel) {
    return "Fuel level is required for this inspection tier.";
  }

  if (requirements.requireWarningLights && input.warningLightsPresent == null) {
    return "Warning lights status is required for this inspection tier.";
  }

  if (requirements.requireFuelLevel && !input.fuelLevel) {
    return "Fuel level is required for this inspection tier.";
  }

  if (requirements.requireObd && (!input.obdFaultCodes || input.obdFaultCodes.length === 0)) {
    return "OBD fault codes capture is required for this inspection tier.";
  }

  for (const category of requirements.requiredCategories) {
    const categoryItems = getChecklistItemsForCategory(category);
    if (requirements.requireAllItemsInCategories) {
      for (const itemName of categoryItems) {
        const item = input.checklistItems.find(
          (checklistItem) => checklistItem.item_name === itemName,
        );
        if (!item) {
          return `Checklist item "${itemName}" is required for this inspection tier.`;
        }
        if (
          requirements.requireDamageNotes &&
          ["poor", "damaged"].includes(item.condition) &&
          !item.remarks?.trim()
        ) {
          return `Damage notes are required for "${itemName}" when condition is poor or damaged.`;
        }
      }
    } else {
      const completedInCategory = categoryItems.filter((itemName) =>
        itemNames.includes(itemName),
      );
      if (completedInCategory.length === 0) {
        return `At least one ${category} checklist item is required for this inspection tier.`;
      }
    }
  }
  return null;
}
